import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wallet, AlertTriangle } from "lucide-react";
import { useCurrencyPref } from "../pages/admin/_shared";

/**
 * Compact header badges — money consumption only.
 *
 * Shows ONLY today's and the current month's spend (admin / super_admin).
 * Regular users don't see anything here; their personal quota lives
 * elsewhere in the UI.
 *
 * Reads /api/credits/consumption.
 */
export default function ConsumptionBadges() {
    const { user: me } = useAuth();
    const [data, setData] = useState(null);
    const [currency] = useCurrencyPref(data?.currency || "USD");
    const isAdmin = me?.role === "admin" || me?.role === "super_admin";

    const load = () => {
        api.get("/credits/consumption").then((r) => setData(r.data)).catch(() => {});
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 20_000);
        const onUpd = () => load();
        window.addEventListener("kc:credits-updated", onUpd);
        return () => {
            clearInterval(t);
            window.removeEventListener("kc:credits-updated", onUpd);
        };
    }, []);

    if (!data) return null;

    const trm = Number(data.trm_cop_per_usd || 4000);
    /**
     * Format a COP-canonical value as either COP or USD based on the user's
     * current preference. Keeps the badges in sync with the platform-wide
     * currency toggle in /admin/configuracion.
     */
    const fmtMoney = (cop) => {
        if (currency === "USD") {
            const usd = trm > 0 ? (cop || 0) / trm : 0;
            return `$${usd.toLocaleString("es-CO", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        }
        return `$${new Intl.NumberFormat("es-CO", {
            maximumFractionDigits: 0,
        }).format(cop || 0)}`;
    };
    const overBudget = data.over_budget;
    const nearLimit = data.percent_used_month >= 80 && !overBudget;

    // Regular users no longer see consumption badges in the header — the
    // money figures are admin-only and the user-level "imgs / cuota" was
    // removed per product decision (header should be minimal).
    if (!isAdmin) {
        return null;
    }

    // Admin / super_admin: only money consumption (today + month). The
    // image counter and per-user quota were intentionally hidden — the
    // header stays focused on the spend signal.
    return (
        <div
            className="flex items-center gap-2 flex-wrap"
            data-testid="consumption-badges"
        >
            <Badge
                testid="badge-cop-today"
                icon={<Wallet className="w-3 h-3" />}
                tone={overBudget ? "danger" : nearLimit ? "warn" : "ok"}
            >
                <span className="font-mono text-[10px] uppercase tracking-widest mr-1.5 opacity-70">
                    Hoy
                </span>
                <strong className="font-display text-sm">{fmtMoney(data.cop_today)}</strong>
            </Badge>
            <Badge
                testid="badge-cop-month"
                tone={overBudget ? "danger" : nearLimit ? "warn" : "neutral"}
                icon={overBudget ? <AlertTriangle className="w-3 h-3" /> : null}
            >
                <span className="font-mono text-[10px] uppercase tracking-widest mr-1.5 opacity-70">
                    Mes
                </span>
                <strong className="font-display text-sm">{fmtMoney(data.cop_month)}</strong>
                <span className="font-mono text-[10px] text-fog-400 ml-1">
                    / {fmtMoney(data.monthly_budget_cop)}
                </span>
            </Badge>
        </div>
    );
}

function Badge({ children, icon, tone = "neutral", testid }) {
    const toneClass =
        tone === "danger"
            ? "border-destructive/50 bg-destructive/10 text-destructive"
            : tone === "warn"
              ? "border-amber-500/50 bg-amber-400/10 text-amber-700"
              : tone === "ok"
                ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700"
                : "border-ink-600 bg-ink-900/50 text-fog-100";
    return (
        <div
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full border hairline px-3 py-1.5 ${toneClass}`}
        >
            {icon}
            {children}
        </div>
    );
}
